import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { X, Loader2, Pill, Package, Snowflake, Lock, AlertTriangle, Info } from 'lucide-react';
// A factory, not a hook, despite the filename — imported under its real name so
// react-hooks/rules-of-hooks does not key on the identifier. See usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';

// Add / edit an entry in the pharmacy's own list. One modal for both: the
// field set is identical and the only differences are the title and the
// endpoint.
//
// MEDICINE OR PRODUCT (owner, 10 Oct 2026) — the same choice as the doctor's
// own list, picked when the row is created and shown, not editable, after. A
// product (a neckband, a thermometer, a diaper pack) has no salt, no dosage
// form, no strength and no schedule, so those fields are hidden for it and the
// server stores it as form 'other', schedule 'none' whatever is sent.
//
// THIS FORM DECIDES WHAT A BILL LOOKS LIKE MONTHS FROM NOW, which is why so
// little of it is free text. `hsnCode` and `gstRate` are copied verbatim onto
// every invoice line this medicine ever appears on, and a GST return is filed
// from those lines — so a rate keyed as "5.5" because somebody guessed produces
// an invoice that cannot be filed and has to be credit-noted one at a time. The
// rate is therefore a list of the legal slabs rather than a number field.
//
// AND THE RATE IS BACKED OUT OF MRP, NOT ADDED TO IT. Indian pharmacy MRP is
// printed tax-inclusive by law, so the billing service computes
// taxable = lineValue / (1 + rate/100) and treats the remainder as tax. Somebody
// reading this form could reasonably assume the opposite and set 0% "because
// the price already includes it" — which would silently declare every sale as
// exempt. The hint under the field says so out loud.

const EMPTY = {
    type: 'medicine',
    name: '', genericName: '', brand: '', manufacturer: '',
    form: 'tablet', strength: '', packSize: '', unit: 'unit',
    hsnCode: '', gstRate: '12', schedule: 'none', locationId: '',
    reorderLevel: '', barcode: '', notes: '',
};

const FORMS = ['tablet', 'capsule', 'syrup', 'injection', 'ointment', 'drops', 'other'];

// The statutory GST slabs. Pharmaceuticals sit at 5% or 12% almost without
// exception; 0% is for the exempt list and 18% for most consumables and devices.
// 28% is offered because the master also carries non-drug counter stock.
const GST_SLABS = ['0', '5', '12', '18', '28'];

// Drugs and Cosmetics Rules, in the order of how much they constrain the
// counter. The dispense endpoint refuses anything other than 'none' unless the
// dispense carries a prescription reference, so this field is not a label — it
// changes what the counter is allowed to do.
const SCHEDULES = [
    { value: 'none', labelKey: 'medicinepage.sched_none', fallback: 'None — over the counter' },
    { value: 'H', labelKey: 'medicinepage.sched_h', fallback: 'H — prescription only' },
    { value: 'H1', labelKey: 'medicinepage.sched_h1', fallback: 'H1 — prescription + bound register' },
    { value: 'X', labelKey: 'medicinepage.sched_x', fallback: 'X — narcotic, double-locked + register' },
];

const BIN_TYPE_SUFFIX = {
    cold_chain: 'cold chain',
    narcotic_cupboard: 'narcotic cupboard',
    otc_front: 'OTC front',
    bulk: 'bulk',
    shelf: '',
};

const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

export default function AddMedicine({ isOpen, onClose, onSaved, editing = null, locations = [], storeName = '' }) {
    const { t } = useTranslation();
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const isEdit = Boolean(editing);
    const isProduct = form.type === 'product';

    useEffect(() => {
        if (!isOpen) return;
        setErrors({});
        setSaving(false);
        if (editing) {
            setForm({
                type: editing.type === 'product' ? 'product' : 'medicine',
                name: editing.name || '',
                genericName: editing.genericName || '',
                brand: editing.brand || '',
                manufacturer: editing.manufacturer || '',
                form: editing.form || 'tablet',
                strength: editing.strength || '',
                packSize: editing.packSize || '',
                unit: editing.unit || 'unit',
                hsnCode: editing.hsnCode || '',
                // The stored rate may predate the slab list (an import, or a rate
                // the government has since retired). Showing it rather than
                // snapping it to the nearest slab keeps an edit of the name from
                // silently re-rating every future bill.
                gstRate: String(Number(editing.gstRate) || 0),
                schedule: editing.schedule || 'none',
                locationId: editing.locationId || '',
                reorderLevel: editing.reorderLevel === null || editing.reorderLevel === undefined ? '' : String(editing.reorderLevel),
                barcode: editing.barcode || '',
                notes: editing.notes || '',
            });
        } else {
            setForm(EMPTY);
        }
    }, [isOpen, editing]);

    // Bins grouped by rack, in the pick order the server already sorted them
    // into. A flat list of forty codes is unusable; the rack is how a pharmacist
    // holds the room in their head.
    //
    // Retired bins are excluded, except the one this medicine is already filed
    // in — dropping that would silently blank the field on an ordinary name
    // edit, which is how a medicine loses its bin without anybody deciding to
    // move it. Same reasoning as assignableRoles in AddUser.jsx.
    const binGroups = useMemo(() => {
        const groups = [];
        const index = new Map();
        (locations || [])
            .filter((l) => l.status === 'active' || l.locationId === editing?.locationId)
            .forEach((l) => {
                const key = l.rackName || '';
                if (!index.has(key)) {
                    const g = { rackName: l.rackName || null, bins: [] };
                    index.set(key, g);
                    groups.push(g);
                }
                index.get(key).bins.push(l);
            });
        return groups;
    }, [locations, editing]);

    const selectedBin = (locations || []).find((l) => l.locationId === form.locationId) || null;

    if (!isOpen) return null;

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = t('medicinepage.name_required', 'Name is required');
        const rate = Number(form.gstRate);
        if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
            e.gstRate = t('medicinepage.gst_invalid', 'GST rate must be a percentage between 0 and 100');
        }
        if (form.reorderLevel !== '') {
            const level = Number(form.reorderLevel);
            if (!Number.isFinite(level) || level < 0) {
                e.reorderLevel = t('medicinepage.reorder_invalid', 'Reorder level cannot be negative');
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;

        // What a product cannot have is not sent, even if it was typed before
        // the type was switched — the hidden fields would otherwise be saved.
        const payload = {
            type: form.type,
            name: form.name.trim(),
            genericName: isProduct ? null : (form.genericName.trim() || null),
            brand: form.brand.trim() || null,
            manufacturer: form.manufacturer.trim() || null,
            form: isProduct ? 'other' : form.form,
            strength: isProduct ? null : (form.strength.trim() || null),
            packSize: form.packSize.trim() || null,
            unit: form.unit.trim() || 'unit',
            hsnCode: form.hsnCode.trim() || null,
            gstRate: Number(form.gstRate) || 0,
            schedule: isProduct ? 'none' : form.schedule,
            // Empty means "no home bin", and null is how the server is told to
            // clear one. Sending '' would be stored as a locationId nothing
            // matches, which prints a blank badge and looks like a bug in the map.
            locationId: form.locationId || null,
            reorderLevel: form.reorderLevel === '' ? 0 : Number(form.reorderLevel),
            barcode: form.barcode.trim() || null,
            notes: form.notes.trim() || null,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await axiosInstance.put(`/pharmacy/medicines/${editing.medicineId}`, payload);
                toast.success(isProduct
                    ? t('medicinepage.update_success_product', 'Product updated')
                    : t('medicinepage.update_success', 'Medicine updated'));
            } else {
                await axiosInstance.post('/pharmacy/medicines', payload);
                toast.success(isProduct
                    ? t('medicinepage.create_success_product', 'Product added to the list')
                    : t('medicinepage.create_success', 'Medicine added to the list'));
            }
            onSaved?.();
            onClose?.();
        } catch (err) {
            toast.error(apiError(err, t('medicinepage.save_error', 'Failed to save the medicine')));
        } finally {
            setSaving(false);
        }
    };

    const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const label = 'text-xs font-medium text-slate-500';

    // A soft warning, never a block. Schedule X has to live behind a double lock
    // with a bound register, and a home bin on an open shelf is the single most
    // common way that rule is broken by accident rather than intent. The hospital
    // may have a cupboard this system does not model, so it says so and lets them
    // save.
    const narcoticMisfiled = !isProduct && form.schedule === 'X' && selectedBin && selectedBin.type !== 'narcotic_cupboard';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="add-medicine-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-2">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isProduct ? 'bg-amber-50' : 'bg-[#4ABDBD]/10'}`}>
                            {isProduct ? <Package className="w-4 h-4 text-amber-600" /> : <Pill className="w-4 h-4 text-[#2D8A8A]" />}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {isEdit
                                    ? (isProduct ? t('medicinepage.edit_product', 'Edit product') : t('medicinepage.edit_medicine', 'Edit medicine'))
                                    : (isProduct ? t('medicinepage.add_product', 'Add product') : t('medicinepage.add_medicine', 'Add medicine'))}
                            </h2>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} data-testid="add-medicine-close" className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Medicine or product — the doctor's own list asks the same
                        question the same way: two buttons on create, a fixed
                        label on edit. */}
                    <div className="sm:col-span-2">
                        <label className={label}>{t('medicinesTab.type_label', 'Type')}</label>
                        {isEdit ? (
                            <div
                                data-testid="add-medicine-type-static"
                                className="mt-1 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 text-[#3AA8A8] text-sm font-semibold"
                            >
                                {isProduct ? <Package className="w-4 h-4 text-amber-500" /> : <Pill className="w-4 h-4 text-[#4ABDBD]" />}
                                {isProduct ? t('medicinesTab.type_product', 'Product') : t('medicinesTab.type_medicine', 'Medicine')}
                            </div>
                        ) : (
                            <div className="mt-1 flex gap-2">
                                {[
                                    { key: 'medicine', label: t('medicinesTab.type_medicine', 'Medicine') },
                                    { key: 'product', label: t('medicinesTab.type_product', 'Product') },
                                ].map((opt) => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        data-testid={`add-medicine-type-${opt.key}`}
                                        aria-pressed={form.type === opt.key}
                                        onClick={() => set('type', opt.key)}
                                        disabled={saving}
                                        className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium transition cursor-pointer disabled:cursor-not-allowed ${
                                            form.type === opt.key
                                                ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]'
                                                : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ABDBD]'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="sm:col-span-2">
                        <label className={label}>{t('medicinepage.name', 'Name as it appears on the pack')} *</label>
                        <input
                            data-testid="add-medicine-name"
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            maxLength={120}
                            className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>

                    {!isProduct && (
                    <div>
                        <label className={label}>{t('medicinepage.generic_name', 'Generic name (salt)')}</label>
                        <input
                            data-testid="add-medicine-generic"
                            value={form.genericName}
                            onChange={(e) => set('genericName', e.target.value)}
                            maxLength={120}
                            className={`${field} border-gray-300`}
                        />
                        {/* A substitution at the counter is judged against the salt,
                            not the trade name — leaving it blank means the
                            pharmacist has nothing to substitute against. */}
                        <p className="text-[11px] text-slate-400 mt-1">
                            {t('medicinepage.generic_hint', 'What a substitution is checked against at the counter.')}
                        </p>
                    </div>
                    )}
                    <div>
                        <label className={label}>{t('medicinepage.brand', 'Brand')}</label>
                        <input
                            data-testid="add-medicine-brand"
                            value={form.brand}
                            onChange={(e) => set('brand', e.target.value)}
                            maxLength={120}
                            className={`${field} border-gray-300`}
                        />
                    </div>

                    <div>
                        <label className={label}>{t('medicinepage.manufacturer', 'Manufacturer')}</label>
                        <input
                            data-testid="add-medicine-manufacturer"
                            value={form.manufacturer}
                            onChange={(e) => set('manufacturer', e.target.value)}
                            maxLength={120}
                            className={`${field} border-gray-300`}
                        />
                    </div>
                    {!isProduct && (
                    <div>
                        <label className={label}>{t('medicinepage.form', 'Form')}</label>
                        <select
                            data-testid="add-medicine-form"
                            value={form.form}
                            onChange={(e) => set('form', e.target.value)}
                            className={`tt-select ${field} border-gray-300 capitalize`}
                        >
                            {FORMS.map((f) => (
                                <option key={f} value={f}>{t(`medicinepage.form_${f}`, f)}</option>
                            ))}
                        </select>
                    </div>
                    )}

                    {!isProduct && (
                    <div>
                        <label className={label}>{t('medicinepage.strength', 'Strength')}</label>
                        <input
                            data-testid="add-medicine-strength"
                            value={form.strength}
                            onChange={(e) => set('strength', e.target.value)}
                            placeholder={t('medicinepage.strength_placeholder', 'e.g. 500 mg')}
                            maxLength={60}
                            className={`${field} border-gray-300`}
                        />
                    </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={label}>{t('medicinepage.pack_size', 'Pack size')}</label>
                            <input
                                data-testid="add-medicine-pack-size"
                                value={form.packSize}
                                onChange={(e) => set('packSize', e.target.value)}
                                placeholder={t('medicinepage.pack_placeholder', 'e.g. 10 x 10')}
                                maxLength={40}
                                className={`${field} border-gray-300`}
                            />
                        </div>
                        <div>
                            <label className={label}>{t('medicinepage.unit', 'Issue unit')}</label>
                            <input
                                data-testid="add-medicine-unit"
                                value={form.unit}
                                onChange={(e) => set('unit', e.target.value)}
                                placeholder={t('medicinepage.unit_placeholder', 'strip / tab / bottle')}
                                maxLength={20}
                                className={`${field} border-gray-300`}
                            />
                        </div>
                    </div>

                    {/* ---- What the bill will say. ---- */}
                    <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                            <h3 className="text-sm font-bold text-[#2D8A8A]">{t('medicinepage.tax_section', 'Tax and pricing')}</h3>
                            <span className="text-[11px] text-slate-400">
                                {t('medicinepage.tax_section_hint', 'Copied onto every invoice line this medicine ever appears on.')}
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className={label}>{t('medicinepage.hsn', 'HSN code')}</label>
                        <input
                            data-testid="add-medicine-hsn"
                            value={form.hsnCode}
                            onChange={(e) => set('hsnCode', e.target.value.replace(/\D/g, '').slice(0, 8))}
                            inputMode="numeric"
                            placeholder="3004"
                            className={`${field} border-gray-300 font-mono`}
                        />
                        <p className="text-[11px] text-slate-400 mt-1">
                            {t('medicinepage.hsn_hint', 'Printed on the tax invoice. Most formulations are 3004.')}
                        </p>
                    </div>
                    <div>
                        <label className={label}>{t('medicinepage.gst_rate', 'GST rate')}</label>
                        <select
                            data-testid="add-medicine-gst"
                            value={form.gstRate}
                            onChange={(e) => set('gstRate', e.target.value)}
                            className={`tt-select ${field} ${errors.gstRate ? 'border-red-400' : 'border-gray-300'}`}
                        >
                            {/* A rate imported from elsewhere that is not a slab is
                                kept selectable rather than silently rounded. */}
                            {!GST_SLABS.includes(form.gstRate) && (
                                <option value={form.gstRate}>{form.gstRate}% ({t('medicinepage.non_standard', 'non-standard')})</option>
                            )}
                            {GST_SLABS.map((r) => (
                                <option key={r} value={r}>{r}%</option>
                            ))}
                        </select>
                        {errors.gstRate
                            ? <p className="text-xs text-red-500 mt-1">{errors.gstRate}</p>
                            : (
                                <p className="text-[11px] text-slate-400 mt-1 flex items-start gap-1">
                                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#4ABDBD]" />
                                    {t('medicinepage.gst_hint', 'MRP is tax-inclusive — this rate is taken OUT of the price, never added on top. Inpatient supply is exempt and bills at zero whatever is set here.')}
                                </p>
                            )}
                    </div>

                    {/* ---- What the counter is allowed to do. ---- */}
                    <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-[#2D8A8A] mb-3">{t('medicinepage.control_section', 'Control and storage')}</h3>
                    </div>

                    {!isProduct && (
                    <div>
                        <label className={label}>{t('medicinepage.schedule', 'Schedule')}</label>
                        <select
                            data-testid="add-medicine-schedule"
                            value={form.schedule}
                            onChange={(e) => set('schedule', e.target.value)}
                            className={`tt-select ${field} border-gray-300`}
                        >
                            {SCHEDULES.map((s) => (
                                <option key={s.value} value={s.value}>{t(s.labelKey, s.fallback)}</option>
                            ))}
                        </select>
                        {form.schedule !== 'none' && (
                            <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {t('medicinepage.schedule_warning', 'The counter will refuse to dispense this without a prescription on file, and will snapshot the prescriber name and registration number onto the bill.')}
                            </p>
                        )}
                    </div>
                    )}

                    <div>
                        <label className={label}>{t('medicinepage.home_bin', 'Home bin')}</label>
                        <select
                            data-testid="add-medicine-location"
                            value={form.locationId}
                            onChange={(e) => set('locationId', e.target.value)}
                            className={`tt-select ${field} border-gray-300`}
                        >
                            <option value="">{t('medicinepage.no_home_bin', 'Not assigned to a bin')}</option>
                            {binGroups.map((g) => (
                                <optgroup key={g.rackName || '_unracked'} label={g.rackName || t('medicinepage.unracked', 'No rack')}>
                                    {g.bins.map((b) => (
                                        <option key={b.locationId} value={b.locationId}>
                                            {b.code}
                                            {BIN_TYPE_SUFFIX[b.type] ? ` — ${t(`rackpage.type_${b.type}`, BIN_TYPE_SUFFIX[b.type])}` : ''}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <p className="text-[11px] text-slate-400 mt-1">
                            {storeName
                                ? t('medicinepage.bin_hint_store', 'Bins at {{store}}. A batch can override this from its own shelf.', { store: storeName })
                                : t('medicinepage.bin_hint', 'Where the pharmacist walks. A batch can override it from its own shelf.')}
                        </p>
                        {selectedBin?.type === 'cold_chain' && (
                            <p className="text-[11px] text-sky-700 mt-1 flex items-start gap-1">
                                <Snowflake className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {t('medicinepage.cold_chain_note', 'Cold chain — this bin is temperature-logged.')}
                            </p>
                        )}
                        {selectedBin?.type === 'narcotic_cupboard' && (
                            <p className="text-[11px] text-rose-700 mt-1 flex items-start gap-1">
                                <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {t('medicinepage.narcotic_note', 'Narcotic cupboard — double-locked, with a bound register.')}
                            </p>
                        )}
                        {narcoticMisfiled && (
                            <p data-testid="add-medicine-narcotic-warning" className="text-[11px] text-rose-700 mt-1 flex items-start gap-1 font-semibold">
                                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {t('medicinepage.narcotic_misfiled', 'Schedule X normally lives in a double-locked narcotic cupboard, not on an open shelf. Save anyway if this hospital keeps it elsewhere.')}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className={label}>{t('medicinepage.reorder_level', 'Reorder level')}</label>
                        <input
                            data-testid="add-medicine-reorder"
                            type="number"
                            min="0"
                            step="1"
                            value={form.reorderLevel}
                            onChange={(e) => set('reorderLevel', e.target.value)}
                            className={`${field} ${errors.reorderLevel ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.reorderLevel
                            ? <p className="text-xs text-red-500 mt-1">{errors.reorderLevel}</p>
                            // Zero is "do not track", not "reorder at zero". A
                            // threshold of zero would put every catalogued-but-never-
                            // stocked product on the buyer's list, and a report that
                            // is mostly noise gets ignored on the day it is right.
                            : <p className="text-[11px] text-slate-400 mt-1">{t('medicinepage.reorder_hint', 'Leave at 0 to leave this medicine off the reorder report.')}</p>}
                    </div>
                    <div>
                        <label className={label}>{t('medicinepage.barcode', 'Barcode')}</label>
                        <input
                            data-testid="add-medicine-barcode"
                            value={form.barcode}
                            onChange={(e) => set('barcode', e.target.value.trim())}
                            maxLength={64}
                            className={`${field} border-gray-300 font-mono`}
                        />
                        <p className="text-[11px] text-slate-400 mt-1">
                            {t('medicinepage.barcode_hint', 'An exact barcode match jumps straight to the top of the counter search.')}
                        </p>
                    </div>

                    <div className="sm:col-span-2">
                        <label className={label}>{t('medicinepage.notes', 'Notes')}</label>
                        <textarea
                            data-testid="add-medicine-notes"
                            value={form.notes}
                            onChange={(e) => set('notes', e.target.value)}
                            rows={2}
                            maxLength={500}
                            className={`${field} border-gray-300 resize-none`}
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('medicinepage.cancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        data-testid="add-medicine-submit"
                        className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isEdit
                            ? t('medicinepage.save', 'Save changes')
                            : (isProduct ? t('medicinepage.create_product', 'Add product') : t('medicinepage.create', 'Add medicine'))}
                    </button>
                </div>
            </div>
        </div>
    );
}
